'use client';

import { c as _c } from "react/compiler-runtime";
import { Alert, Button, Checkbox, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Radio, RadioGroup, useDisclosure } from '@heroui/react';
import { LucideInfo } from 'lucide-react';
import { useMemo } from 'react';
import { fixoRegex } from '@/shared/constants/fixo-regex';
import { whatsappRegex } from '@/shared/constants/whatsapp-regex';
import removeNonNumericCharacters from '@/shared/functions/remove-non-numeric-characters';
import { AuthForm } from '../../../components/form/auth-form';
import type { usePreRegisterForm } from '../../hooks/use-pre-register-form';
import type { CreatePreRegistrationForm } from '../../interfaces/create-pre-registration-form';
import { PRE_REGISTER_LGPD_LABEL, PRE_REGISTER_PROFESSIONS } from '../../shared/constants/pre-register-professions';
type PreRegisterFormFieldsProps = Pick<ReturnType<typeof usePreRegisterForm>, 'preRegistration' | 'hasAddress' | 'canSubmit' | 'handleInputChange' | 'handlePhoneChange' | 'handleSubmit' | 'isLoading' | 'formatPhoneNumber' | 'addAddressModal'>;
function toNationalContact(value: string): string {
  const digits = removeNonNumericCharacters(value);
  return digits.startsWith('55') ? digits.slice(2) : digits;
}
export function PreRegisterFormFields(t0) {
  const $ = _c(72);
  const {
    preRegistration,
    hasAddress,
    canSubmit,
    handleInputChange,
    handlePhoneChange,
    handleSubmit,
    isLoading,
    formatPhoneNumber,
    addAddressModal
  } = t0;
  const phoneFormatModal = useDisclosure();
  const nationalContact = toNationalContact(preRegistration.contact);
  const isWhatsapp = whatsappRegex.test(nationalContact);
  const isFixo = fixoRegex.test(nationalContact);
  const phoneTypeLabel = isWhatsapp ? "celular" : isFixo ? "fixo" : "";
  let t1;
  if ($[0] !== handleInputChange) {
    t1 = e => handleInputChange("name", e.target.value);
    $[0] = handleInputChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== preRegistration.name || $[3] !== t1) {
    t2 = <Input isRequired={true} required={true} value={preRegistration.name} onChange={t1} variant="underlined" type="text" name="name" label="Nome completo" />;
    $[2] = preRegistration.name;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== handleInputChange) {
    t3 = e_0 => handleInputChange("email", e_0.target.value);
    $[5] = handleInputChange;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== preRegistration.email || $[8] !== t3) {
    t4 = <Input isRequired={true} required={true} value={preRegistration.email} onChange={t3} variant="underlined" type="email" name="email" label="E-mail" />;
    $[7] = preRegistration.email;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== formatPhoneNumber || $[11] !== preRegistration.contact) {
    t5 = formatPhoneNumber(preRegistration.contact);
    $[10] = formatPhoneNumber;
    $[11] = preRegistration.contact;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = `Telefone${phoneTypeLabel ? ` (${phoneTypeLabel})` : ""}`;
  const t7 = phoneTypeLabel ? `Adicionando número de telefone ${phoneTypeLabel}` : undefined;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <LucideInfo size={18} />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== phoneFormatModal.onOpen) {
    t9 = <Button isIconOnly={true} size="sm" variant="light" aria-label="Como formatar o telefone" onPress={phoneFormatModal.onOpen} className="text-default-500">{t8}</Button>;
    $[14] = phoneFormatModal.onOpen;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== handlePhoneChange || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t9) {
    t10 = <Input isRequired={true} required={true} value={t5} onChange={handlePhoneChange} variant="underlined" placeholder="(__) _ ____-____" autoComplete="off" name="contact" label={t6} type="text" inputMode="numeric" maxLength={18} description={t7} endContent={t9} />;
    $[16] = handlePhoneChange;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ModalHeader>Formatação do telefone</ModalHeader>;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      base: "grow-0"
    };
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = isWhatsapp || isFixo ? "success" : "default";
  let t14;
  if ($[24] !== t13) {
    t14 = <ModalBody><Alert classNames={t12} title={"Formata\xE7\xE3o"} color={t13} description="(DD) 9 XXXX-XXXX para celular ou (DD) XXXX-XXXX para fixo." /></ModalBody>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== phoneFormatModal.onClose) {
    t15 = <ModalFooter><Button variant="light" onPress={phoneFormatModal.onClose}>Fechar</Button></ModalFooter>;
    $[26] = phoneFormatModal.onClose;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t14 || $[29] !== t15) {
    t16 = <ModalContent>{t11}{t14}{t15}</ModalContent>;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== phoneFormatModal.isOpen || $[32] !== phoneFormatModal.onOpenChange || $[33] !== t16) {
    t17 = <Modal backdrop="blur" size="sm" isOpen={phoneFormatModal.isOpen} onOpenChange={phoneFormatModal.onOpenChange}>{t16}</Modal>;
    $[31] = phoneFormatModal.isOpen;
    $[32] = phoneFormatModal.onOpenChange;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const t18 = hasAddress ? "Trocar endere\xE7o" : "Adicionar endere\xE7o";
  let t19;
  if ($[35] !== addAddressModal.onOpen || $[36] !== isLoading || $[37] !== t18) {
    t19 = <Button size="lg" variant="bordered" className="w-full border-default-300 font-medium" isDisabled={isLoading} onPress={addAddressModal.onOpen}>{t18}</Button>;
    $[35] = addAddressModal.onOpen;
    $[36] = isLoading;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== hasAddress || $[40] !== preRegistration.address || $[41] !== preRegistration.city || $[42] !== preRegistration.neighborhood || $[43] !== preRegistration.state) {
    t20 = hasAddress && <p className="line-clamp-2 text-sm font-dm_sans text-default-600">{preRegistration.address}, {preRegistration.neighborhood}, {preRegistration.city}, {preRegistration.state}</p>;
    $[39] = hasAddress;
    $[40] = preRegistration.address;
    $[41] = preRegistration.city;
    $[42] = preRegistration.neighborhood;
    $[43] = preRegistration.state;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      base: "my-2",
      label: "text-foreground font-medium",
      wrapper: "flex flex-wrap gap-6"
    };
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = preRegistration.profession;
  let t23;
  if ($[46] !== handleInputChange) {
    t23 = value => handleInputChange("profession", value as CreatePreRegistrationForm['profession']);
    $[46] = handleInputChange;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = PRE_REGISTER_PROFESSIONS.map(_temp);
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== preRegistration.profession || $[50] !== t23) {
    t25 = <RadioGroup label={"Qual sua profiss\xE3o?"} orientation="horizontal" classNames={t21} value={t22} onValueChange={t23}>{t24}</RadioGroup>;
    $[49] = preRegistration.profession;
    $[50] = t23;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== handleInputChange) {
    t26 = value_0 => handleInputChange("acceptedLgpd", value_0);
    $[52] = handleInputChange;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      base: "items-start",
      label: "text-sm leading-relaxed text-default-700"
    };
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== preRegistration.acceptedLgpd || $[56] !== t26) {
    t28 = <Checkbox isRequired={true} isSelected={preRegistration.acceptedLgpd} onValueChange={t26} classNames={t27} radius="full">{PRE_REGISTER_LGPD_LABEL}</Checkbox>;
    $[55] = preRegistration.acceptedLgpd;
    $[56] = t26;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  const t29 = !canSubmit;
  let t30;
  if ($[58] !== isLoading || $[59] !== t29) {
    t30 = <Button type="submit" variant="solid" isLoading={isLoading} isDisabled={t29} className="mx-auto w-full bg-foreground text-background font-medium">Cadastrar-me</Button>;
    $[58] = isLoading;
    $[59] = t29;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== handleSubmit || $[62] !== t10 || $[63] !== t17 || $[64] !== t19 || $[65] !== t2 || $[66] !== t20 || $[67] !== t25 || $[68] !== t28 || $[69] !== t30 || $[70] !== t4) {
    t31 = <AuthForm.Form onSubmit={handleSubmit} className="gap-6">{t2}{t4}{t10}{t17}{t19}{t20}{t25}{t28}{t30}</AuthForm.Form>;
    $[61] = handleSubmit;
    $[62] = t10;
    $[63] = t17;
    $[64] = t19;
    $[65] = t2;
    $[66] = t20;
    $[67] = t25;
    $[68] = t28;
    $[69] = t30;
    $[70] = t4;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  return t31;
}
function _temp(profession) {
  return <Radio key={profession.value} value={profession.value} classNames={{
    label: "text-sm"
  }}>{profession.label}</Radio>;
}